import { useState } from 'react';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { EDTextEdit } from '../TextEdit/EDTextEdit';
import EDIconButton from '../Buttons/IconButton/EDIconButton';
import { findFormatter } from '../../Utils/Formatters/FindFormatter';
import { TEDFormatInt } from '../../Utils/Formatters/FormatterClasses/TEDFormatInt';
import { ParseResult } from '../../Utils/Formatters/FormatterTypes';
import { ClientAttributeDataType } from '../../Types/ClientModelTypes';

interface IProps {
  tooltip?: string;
  value: number | null;
  onChange: (newValue: number | null) => void;
  formatterType?: string;
  dataType?: ClientAttributeDataType;
  marker?: boolean;
  label: string;
  error?: boolean;
  warning?: boolean;
  hint?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  hideButtons?: boolean;
  simpleLine?: boolean;
  outlined?: boolean;
  classes?: Partial<Record<keyof ReturnType<typeof styles>, string>>;
  numericOnly?: boolean;
  testId?: string;
}

const styles = (theme: Theme) => ({
  rootContainer: {
    display: 'flex',
    width: '100%',
  },
  editorContainer: {
    display: 'flex',
    flex: 1,
    width: '200px',
  },
  buttonContainer: {
    display: 'flex',
    alignSelf: 'start',
    paddingTop: 8,
  },
});

const EDCounterUnstyled: React.FC<React.PropsWithChildren<IProps>> = props => {
  const [error, setError] = useState('');

  const formatter =
    (props.formatterType &&
      findFormatter(props.formatterType, props.dataType)) ||
    new TEDFormatInt();

  const increment = () => {
    let cnt = (props.value || 0) + 1;
    props.onChange(cnt);
  };

  const decrement = () => {
    let cnt = (props.value || 0) - 1;
    if (cnt >= 0) {
      props.onChange(cnt);
    }
  };

  const onChange = (value: string | null) => {
    const sanitizedValue = props.numericOnly
      ? value?.replace(/[^\d]/g, '') || ''
      : value || '';

    let res = formatter.parser.parse(sanitizedValue);
    if (res.result === ParseResult.OK) {
      setError('');
      props.onChange(res.value as number | null);
    } else {
      setError(sanitizedValue);
    }
  };

  const simpleLine =
    props.simpleLine !== undefined ? { simpleLine: props.simpleLine } : {};
  const outlined =
    props.outlined !== undefined ? { outlined: props.outlined } : {};
  const classes = withStyles.getClasses(props);
  return (
    <div className={classes.rootContainer}>
      <div className={classes.editorContainer}>
        <EDTextEdit
          value={
            error !== '' ? error : formatter.displayer.display(props.value)
          }
          marker={props.marker}
          label={props.label}
          helperText={error !== '' ? 'Ogiltigt värde' : props.helperText}
          tooltip={props.tooltip}
          disabled={props.disabled}
          required={props.required}
          {...simpleLine}
          {...outlined}
          fullWidth={true}
          onChange={onChange}
          error={error !== '' || props.error}
          warning={props.warning}
          hint={props.hint}
          testId={props.testId}
        />
      </div>
      {!props.hideButtons && (
        <>
          <div className={classes.buttonContainer}>
            <EDIconButton
              incrementer
              noRightMargin
              icon="remove"
              tooltip={'Minska ' + (props.tooltip || 'värde')}
              disableTabbing
              disabled={!props.value || props.disabled}
              onClick={decrement}
            />
          </div>
          <div className={classes.buttonContainer}>
            <EDIconButton
              incrementer
              noRightMargin
              icon="add"
              tooltip={'Öka ' + (props.tooltip || 'värde')}
              disableTabbing
              disabled={props.disabled}
              onClick={increment}
            />
          </div>
        </>
      )}
    </div>
  );
};

export const EDCounter = withStyles(EDCounterUnstyled, styles);
